import { useEffect, useMemo, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { CarryoverDialog } from "@/components/CarryoverDialog";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function HolidayRequests() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const load = () => api.get("/leave-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action, reason) => {
    try { await api.post(`/leave-requests/${id}/${action}`, reason ? { reason } : {}); toast.success(`Leave ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const filtered = rows.filter((r) => tab === "all" ? true : r.status === tab);

  // Calendar: map date -> [leave] for approved+pending in this month
  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const startPad = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const onLeave = rows.filter((r) => ["approved", "pending"].includes(r.status) && r.start_date <= iso && r.end_date >= iso);
      cells.push({ d, iso, onLeave });
    }
    return cells;
  }, [cursor, rows]);

  const shift = (delta) => setCursor((c) => { let m = c.m + delta, y = c.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } return { y, m }; });

  return (
    <AdminLayout nav={adminNav} title="Holiday Requests">
      <PageHeader title="Holiday Requests" subtitle="Approve, reject and check clashes">
        <CarryoverDialog />
        <Tabs value={view} onValueChange={setView}>
          <TabsList><TabsTrigger value="list" data-testid="view-list">List</TabsTrigger><TabsTrigger value="calendar" data-testid="view-calendar">Calendar</TabsTrigger></TabsList>
        </Tabs>
      </PageHeader>

      {view === "list" ? (
        <>
          <Tabs value={tab} onValueChange={setTab} className="mb-4">
            <TabsList>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="p-3">Employee</th><th className="p-3">Type</th><th className="p-3">Dates</th><th className="p-3 text-right">Days</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-400">No requests.</td></tr>}
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`leave-row-${r.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                    <td className="p-3 font-medium">{r.employee_name}</td>
                    <td className="p-3">{r.leave_type}</td>
                    <td className="p-3 num">{fmtDate(r.start_date)} – {fmtDate(r.end_date)}</td>
                    <td className="p-3 num text-right">{r.working_days}</td>
                    <td className="p-3"><StatusBadge status={r.status} /></td>
                    <td className="p-3 text-right">
                      {r.status === "pending" && (
                        <div className="flex gap-1 justify-end">
                          <Button data-testid={`approve-leave-${r.id}`} size="sm" className="bg-emerald-600 text-white h-8" onClick={() => act(r.id, "approve")}>Approve</Button>
                          <Button data-testid={`reject-leave-${r.id}`} size="sm" variant="outline" className="h-8 text-rose-600" onClick={() => act(r.id, "reject", "Not approved")}>Reject</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => shift(-1)} className="p-2 border border-zinc-200 rounded-md"><ChevronLeft className="h-4 w-4" /></button>
            <div className="font-heading font-bold text-lg">{MONTHS[cursor.m]} {cursor.y}</div>
            <button data-testid="cal-next" onClick={() => shift(1)} className="p-2 border border-zinc-200 rounded-md"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((c, i) => (
              <div key={i} data-testid={c ? `cal-day-${c.iso}` : undefined} className={`min-h-[84px] rounded-md border p-1 ${c ? "border-zinc-100 bg-white" : "border-transparent"} ${c && c.onLeave.length ? "bg-amber-50/60 border-amber-200" : ""}`}>
                {c && <>
                  <div className="text-xs font-semibold text-zinc-500">{c.d}</div>
                  <div className="space-y-0.5 mt-0.5">
                    {c.onLeave.slice(0, 3).map((l) => (
                      <div key={l.id} className={`text-[10px] truncate px-1 rounded ${l.status === "approved" ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"}`} title={`${l.employee_name} · ${l.leave_type}`}>{l.employee_name}</div>
                    ))}
                    {c.onLeave.length > 3 && <div className="text-[10px] text-zinc-500">+{c.onLeave.length - 3} more</div>}
                  </div>
                </>}
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-200 inline-block" /> Approved</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-200 inline-block" /> Pending</span>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
